import React, { useState } from "react";
import { Reveal } from "./Reveal";
import { ClippedImage } from "./Manifesto";
import { Modal, RegistrationForm } from "./Forms";
import { ArrowUpRight } from "lucide-react";

const CATEGORIES = [
  "Contemporary Womenswear", "Menswear", "Indo-Western", "Resort Wear",
  "Luxury Khadi", "Streetwear", "Accessories", "Sustainable Lifestyle Products",
];

const MASTERCLASSES = [
  "Sustainable Fashion", "Fashion Entrepreneurship", "Textile Innovation",
  "Fashion Marketing", "Branding", "Garment Production",
  "Styling", "Fashion Photography", "Runway Choreography",
];

const AMBASSADORS = [
  "National Fashion Icons", "Leading Designers", "Bollywood Celebrities",
  "Supermodels", "Sustainability Leaders", "Textile Experts",
];

const MEDIA = [
  "Television", "Print Media", "Fashion Magazines", "Digital Media", "OTT Content",
  "Social Media Creators", "Campus Ambassadors", "Live Streaming", "Documentary Film",
];

const CSR = [
  "Rural Livelihood Development", "Women Empowerment", "Skill Development", "Youth Engagement",
  "Sustainable Consumption", "Traditional Craft Preservation", "Employment Generation", "Entrepreneurship Promotion",
];

export const DesignChallenge = ({ openRef }) => {
  const [open, setOpen] = useState(false);
  // expose opener to parent via ref callback
  if (openRef) openRef.current = () => setOpen(true);

  return (
    <section id="challenge" data-testid="challenge-section" className="relative khadi-tex bg-[#f2ece0] py-28 md:py-40 px-5 md:px-10">
      <div className="mx-auto max-w-[1600px]">
        <div className="grid md:grid-cols-12 gap-12 items-start">
          <div className="md:col-span-5 md:sticky md:top-28">
            <Reveal>
              <p className="font-display text-[11px] tracking-[0.3em] uppercase text-[#a65a34] mb-6">(04) Innovation Program</p>
              <h2 className="font-serif text-5xl md:text-7xl leading-[0.92] tracking-tight">
                The Khadi Design Innovation Challenge
              </h2>
              <p className="mt-8 font-body text-black/60 leading-relaxed max-w-md">
                Design full collections across eight categories. The finest work is professionally
                produced and showcased on the runway of the National Finale.
              </p>
              <button
                data-testid="open-registration-btn"
                onClick={() => setOpen(true)}
                className="btn-invert group mt-10 inline-flex items-center gap-3 border border-[#2a2018] px-8 py-4 font-display text-[12px] uppercase tracking-[0.25em] text-[#2a2018] transition-colors duration-300 hover:text-[#f2ece0]"
              >
                <span className="fill" />
                Enter the Challenge
                <ArrowUpRight size={16} className="transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1" />
              </button>
            </Reveal>
            <Reveal delay={0.2} className="mt-12 hidden md:grid grid-cols-2 gap-3">
              <ClippedImage
                src="https://images.pexels.com/photos/9020697/pexels-photo-9020697.jpeg?auto=compress&cs=tinysrgb&w=900"
                alt="Indian male model in contemporary khadi kurta"
                ratio="aspect-[3/4]"
              />
              <ClippedImage
                src="https://images.pexels.com/photos/27139274/pexels-photo-27139274.jpeg?auto=compress&cs=tinysrgb&w=900"
                alt="Indian female model in contemporary saree streetwear"
                ratio="aspect-[3/4]"
              />
            </Reveal>
          </div>

          <div className="md:col-span-7">
            {CATEGORIES.map((c, i) => (
              <Reveal key={c} delay={i * 0.04}>
                <div className="group relative flex items-center justify-between border-t border-black/10 py-7 md:py-9 last:border-b transition-colors duration-500 hover:border-[#a65a34] cursor-default">
                  <div className="flex items-baseline gap-6">
                    <span className="font-display text-xs text-black/30">{String(i + 1).padStart(2, "0")}</span>
                    <h3 className="font-serif text-3xl md:text-5xl tracking-tight transition-transform duration-500 group-hover:translate-x-2">{c}</h3>
                  </div>
                  <ArrowUpRight size={22} className="text-black/20 transition-all duration-500 group-hover:text-[#a65a34] group-hover:rotate-0 -rotate-45" />
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </div>

      <Modal open={open} onClose={() => setOpen(false)} testid="registration-modal" subtitle="Design Innovation Challenge" title="Enter the Challenge">
        <RegistrationForm onSuccess={() => setOpen(false)} />
      </Modal>
    </section>
  );
};

export const KnowledgeSeries = () => (
  <section id="knowledge" data-testid="knowledge-section" className="relative khadi-tex-dark bg-[#241a10] text-[#f2ece0] py-28 md:py-40 px-5 md:px-10 grain">
    <div className="mx-auto max-w-[1600px]">
      <Reveal>
        <p className="font-display text-[11px] tracking-[0.3em] uppercase text-[#a65a34] mb-6">(05) Knowledge Series</p>
        <h2 className="font-serif text-4xl md:text-7xl leading-[0.98] tracking-tight max-w-4xl">
          Masterclasses led by the people shaping Indian fashion.
        </h2>
      </Reveal>
      <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-x-12">
        {MASTERCLASSES.map((m, i) => (
          <Reveal key={m} delay={(i % 3) * 0.06}>
            <div className="group flex items-baseline gap-5 border-t border-[#f2ece0]/15 py-6 transition-colors duration-500 hover:border-[#a65a34]">
              <span className="font-serif italic text-2xl text-[#f2ece0]/30 group-hover:text-[#a65a34] transition-colors">{String(i + 1).padStart(2, "0")}</span>
              <h3 className="font-display text-base md:text-lg uppercase tracking-tight">{m}</h3>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

export const Ambassadors = () => (
  <section data-testid="ambassadors-section" className="relative khadi-tex bg-[#f2ece0] py-28 md:py-36 px-5 md:px-10">
    <div className="mx-auto max-w-[1600px] grid md:grid-cols-12 gap-12 items-center">
      <div className="md:col-span-5">
        <ClippedImage
          src="https://images.pexels.com/photos/13624155/pexels-photo-13624155.jpeg?auto=compress&cs=tinysrgb&w=1200"
          alt="Indian male model in contemporary khadi Nehru jacket"
          ratio="aspect-[3/4]"
        />
      </div>
      <div className="md:col-span-7 md:pl-10">
        <Reveal>
          <p className="font-display text-[11px] tracking-[0.3em] uppercase text-[#a65a34] mb-6">(06) Brand Ambassadors</p>
          <h2 className="font-serif text-4xl md:text-6xl leading-[0.98] tracking-tight">
            The faces amplifying Khadi to India&apos;s youth.
          </h2>
        </Reveal>
        <div className="mt-10 flex flex-wrap gap-3">
          {AMBASSADORS.map((a, i) => (
            <Reveal key={a} delay={i * 0.05}>
              <span className="inline-block border border-black/15 px-6 py-3 font-display text-xs uppercase tracking-[0.15em] transition-colors duration-300 hover:bg-[#1a130b] hover:text-[#f2ece0]">
                {a}
              </span>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export const MediaAndCSR = () => (
  <section data-testid="media-csr-section" className="relative khadi-tex-dark bg-[#1a130b] text-[#f2ece0] py-28 md:py-40 px-5 md:px-10 grain">
    <div className="mx-auto max-w-[1600px] grid md:grid-cols-2 gap-20 md:gap-16">
      <div>
        <Reveal>
          <p className="font-display text-[11px] tracking-[0.3em] uppercase text-[#a65a34] mb-6">(07) National Media Campaign</p>
          <h2 className="font-serif text-4xl md:text-6xl leading-[0.98] tracking-tight mb-10">A movement, everywhere.</h2>
        </Reveal>
        <div className="grid grid-cols-2 gap-x-8">
          {MEDIA.map((m, i) => (
            <Reveal key={m} delay={i * 0.04}>
              <div className="border-t border-[#f2ece0]/15 py-4 font-body text-sm md:text-base text-[#f2ece0]/80">{m}</div>
            </Reveal>
          ))}
        </div>
      </div>
      <div>
        <Reveal>
          <p className="font-display text-[11px] tracking-[0.3em] uppercase text-[#a65a34] mb-6">(08) CSR Alignment</p>
          <h2 className="font-serif text-4xl md:text-6xl leading-[0.98] tracking-tight mb-10">Impact your brand can stand behind.</h2>
        </Reveal>
        <div className="grid grid-cols-1 gap-0">
          {CSR.map((c, i) => (
            <Reveal key={c} delay={i * 0.04}>
              <div className="group flex items-center gap-4 border-t border-[#f2ece0]/15 py-4 transition-colors hover:border-[#a65a34]">
                <span className="h-1.5 w-1.5 rounded-full bg-[#a65a34]" />
                <span className="font-body text-sm md:text-base text-[#f2ece0]/80 group-hover:text-[#f2ece0] transition-colors">{c}</span>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </div>
  </section>
);

export default DesignChallenge;
